import { 
  Box, 
  FormControl, 
  MenuItem, 
  Paper, 
  Select, 
  SelectChangeEvent,
  TextField, 
  Typography, 
  useTheme, 
  Autocomplete,
  Chip
} from '@mui/material';
import { IMultiSectionUnitConfiguration } from "../../../Models/IUnitConfiguration";
import { PropertyConstants } from "../../../Models/Constants";
import React from 'react';

/**
 * Input/Output option item interface
 */
interface InputOutputOption {
  /** Unique identifier for the option */
  key: string;
  /** Display text for the option */
  text: string;
  /** Whether this item is a category header */
  isHeader?: boolean;
}

/**
 * Props for property selection via autocomplete
 */
interface PropertySelectorProps {
  /** Label for the field */
  label: string;
  /** Available options */
  options: InputOutputOption[];
  /** Currently selected values */
  selectedKeys?: string[];
  /** Keys to exclude from options */
  excludeKeys?: string[];
  /** Callback when selection changes */
  onChange: (keys: string[]) => void;
  /** Callback when field is focused */
  onFocus?: () => void;
  /** Custom styles */
  sx?: Record<string, any>;
  /** Number of visible tags before +X */
  limitTags?: number;
}

/**
 * Props for the section configuration component
 */
interface MultiSectionUnitConfigurationProps {
  /** Section configuration data */
  section: IMultiSectionUnitConfiguration;
  /** Index in the array of sections */
  index: number;
  /** Display index (might differ from array index) */
  displayIndex: number;
  /** Available input/output options */
  inputOutputOptions?: InputOutputOption[];
  /** Callback when a property changes */
  onChange: (property: string, value: any, index: number) => void;
  /** Callback when the section is clicked */
  onClick?: (index: number) => void;
}

/**
 * Prevents specific keys from being entered in text fields
 */
const disableSlashKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key === '/') {
    event.preventDefault();
  }
};

/**
 * PropertySelector component for selecting multiple properties from a list
 */
const PropertySelector: React.FC<PropertySelectorProps> = ({
  label,
  options,
  selectedKeys = [],
  excludeKeys = [],
  onChange,
  onFocus,
  sx,
  limitTags = 3
}) => {
  const theme = useTheme();
  
  // Filter out headers and excluded keys
  const filteredOptions = options.filter(option => 
    !option.isHeader && !excludeKeys.includes(option.key)
  );
  
  // Get the option group for proper grouping in select
  const getOptionGroup = (option: InputOutputOption): string => {
    const optionIndex = options.findIndex(o => o.key === option.key);
    if (optionIndex === -1) return '';
    
    // Look backwards for the closest header
    for (let i = optionIndex; i >= 0; i--) {
      if (options[i].isHeader) {
        return options[i].text;
      }
    }
    
    return '';
  };
  
  // Check if option belongs to Hot media category
  const isHotMediaOption = (option: InputOutputOption): boolean => {
    const group = getOptionGroup(option);
    return group.toLowerCase().includes('hot');
  };
  
  return (
    <Box sx={{ ...sx }}>
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'bold', 
          mb: 0.5, 
          color: theme.palette.text.primary,
          fontSize: '0.875rem'
        }}
      >
        {label}
      </Typography>
      
      <Autocomplete
        multiple
        size="small"
        options={filteredOptions}
        getOptionLabel={(option) => option.text}
        groupBy={getOptionGroup}
        isOptionEqualToValue={(option, value) => option.key === value.key}
        value={filteredOptions.filter(option => selectedKeys.includes(option.key))}
        onChange={(_, newValue) => {
          onChange(newValue.map(item => item.key));
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            placeholder={selectedKeys.length === 0 ? `Select ${label.toLowerCase()}...` : ''}
            onFocus={onFocus}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.text.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                }
              }
            }}
          />
        )}
        renderTags={(value, getTagProps) => 
          value.map((option, index) => {
            const isHot = isHotMediaOption(option);
            const hotMediaColor = '#E84745';
            const coldMediaColor = '#007fc8';
            
            return (
              <Chip
                {...getTagProps({ index })}
                key={option.key}
                label={option.text}
                size="small"
                sx={{
                  backgroundColor: isHot ? hotMediaColor : coldMediaColor,
                  color: '#FFFFFF',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  height: '20px',
                  '& .MuiChip-deleteIcon': {
                    color: '#FFFFFF',
                    '&:hover': {
                      color: isHot ? '#C63533' : theme.palette.primary.dark,
                    }
                  }
                }}
              />
            );
          })
        }
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            // Nieaktywny: tylko 1 rząd
            maxHeight: '40px',
            overflow: 'hidden',
            transition: 'max-height 0.3s ease',
            
            // Aktywny: pełna wysokość
            '&.Mui-focused, &:focus-within': {
              maxHeight: 'none',
              overflow: 'visible'
            }
          }
        }}
      />
    </Box>
  );
};

/**
 * MediaTypeField component for displaying and editing media types
 */
const MediaTypeField: React.FC<{
  label: string;
  value?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  color: string;
}> = ({ label, value = '', onChange, onFocus, color }) => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      flex: 1, 
      mb: { xs: 1, sm: 1, md: 0 },
      minWidth: 0 // Allow to shrink below minimum width
    }}>
      <Typography 
        variant="body2" 
        sx={{ 
          color, 
          fontWeight: 'bold', 
          mb: 0.5,
          fontSize: '0.875rem'
        }}
      >
        {label}
      </Typography>
      
      <TextField
        fullWidth
        size="small"
        value={value}
        onFocus={onFocus}
        onKeyPress={disableSlashKey}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${label.toLowerCase()}...`}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.text.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: color,
              borderWidth: 1,
            }
          }
        }}
      />
    </Box>
  );
};

/**
 * MultiSectionUnitConfiguration component for displaying and editing a section of a unit configuration
 */
export const MultiSectionUnitConfiguration: React.FC<MultiSectionUnitConfigurationProps> = ({
  section,
  index,
  displayIndex,
  inputOutputOptions = [],
  onChange,
  onClick
}) => {
  const theme = useTheme();
  
  // Helper function to handle property changes
  const handlePropertyChange = (property: string, value: any) => {
    // Check if this is an Inputs or Outputs update
    if (property === PropertyConstants.Inputs || property === PropertyConstants.Outputs) {
      // For inputs/outputs, value is now an array of keys
      onChange(property, value, index);
    } else {
      // For other properties, pass the value directly
      onChange(property, value, index);
    }
  };
  
  // Handle changes to calculation type
  const handleCalculationTypeChange = (event: SelectChangeEvent<string>) => {
    handlePropertyChange(PropertyConstants.CalculationType, event.target.value);
  };
  
  // Handle changes to inputs
  const handleInputsChange = (keys: string[]) => {
    // We need to update the entire array at once, not just add one item
    handlePropertyChange(
      PropertyConstants.Inputs, 
      keys
    );
  };
  
  // Handle changes to outputs
  const handleOutputsChange = (keys: string[]) => {
    // We need to update the entire array at once, not just add one item
    handlePropertyChange(
      PropertyConstants.Outputs, 
      keys
    );
  };
  
  // Focus handler
  const handleFocus = () => {
    if (onClick) onClick(index);
  };
  
  // Styles organized by component/section
  const styles = {
    // Container styles
    container: {
      p: 2,
      borderLeft: `5px solid ${theme.palette.primary.main}`,
      borderRadius: 1,
      boxShadow: theme.shadows[2],
      bgcolor: 'background.paper',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: theme.shadows[4],
        transform: 'translateY(-2px)',
      }
    },
    
    // Header styles
    header: {
      display: 'flex',
      flexDirection: 'column', // Always stack vertically
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      mb: 2,
      gap: 1
    },
    sectionTitle: {
      fontWeight: 'bold',
      mb: 1, // Consistent margin
      color: theme.palette.primary.main,
      width: '100%'
    },
    calculationTypeContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: '100%' // Always take full width
    },
    calculationTypeLabel: {
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      minWidth: '95px' // Ensure consistent width for label
    },
    calculationTypeSelect: {
      flex: 1, // Take remaining space
      minWidth: 0, // Allow to shrink below minimum width
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.text.primary,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        }
      }
    },
    
    // Media types section
    mediaTypesContainer: {
      display: 'flex',
      flexDirection: { xs: 'column', sm: 'column', md: 'row' }, // Stack vertically on small and medium screens
      gap: 2,
      mb: 2,
      width: '100%'
    },
    
    // Form section
    formSection: {
      width: '100%'
    },
    
    // Input field styling
    inputField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        borderColor: theme.palette.divider,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.text.primary,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: 1,
        }
      }
    }
  };

  return (
    <Paper 
      sx={styles.container}
      onClick={handleFocus}
      elevation={2}
    >
      {/* Header with section number and calculation type */}
      <Box sx={styles.header}>
        <Typography variant="subtitle1" sx={styles.sectionTitle}>
          Section {section.sectionNumber}
        </Typography>
        
        <Box sx={styles.calculationTypeContainer}>
          <Typography variant="body2" sx={styles.calculationTypeLabel}>
            Calculation type:
          </Typography>
          
          <FormControl size="small" sx={styles.calculationTypeSelect}>
            <Select
              value={section.calculationType || ''}
              onChange={handleCalculationTypeChange}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200
                  }
                }
              }}
            >
              <MenuItem value="Performance">Performance</MenuItem>
              <MenuItem value="Rating">Rating</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Media types */}
      <Box sx={styles.mediaTypesContainer}>
        <MediaTypeField
          label="Hot media type"
          value={section.hotMediaType}
          color={theme.palette.error.main}
          onFocus={handleFocus}
          onChange={(value) => handlePropertyChange(PropertyConstants.HotMediaType, value)}
        />
        
        <MediaTypeField
          label="Cold media type"
          value={section.coldMediaType}
          color={theme.palette.primary.main}
          onFocus={handleFocus}
          onChange={(value) => handlePropertyChange(PropertyConstants.ColdMediaType, value)}
        />
      </Box>

      {/* Form sections with consistent spacing */}
      <Box sx={styles.formSection}>
        {/* Inputs */}
        <PropertySelector
          label="Inputs"
          options={inputOutputOptions}
          selectedKeys={section.inputs}
          excludeKeys={section.outputs}
          onChange={handleInputsChange}
          onFocus={handleFocus}
        />

        {/* Outputs */}
        <PropertySelector
          label="Outputs"
          options={inputOutputOptions}
          selectedKeys={section.outputs}
          excludeKeys={section.inputs}
          onChange={handleOutputsChange}
          onFocus={handleFocus}
          sx={{ mt: 2 }}
        />
      </Box>
    </Paper>
  );
}; 